// src/api/locationsApi.ts

export type StoreLocation = {
  id: string;
  name: string;
  number?: string;
  hours: string[];
  location: string;
  map?: string;
  mapImg?: string;     // NOTE: backend GET returns a URL here (in your current setup)
  storeImg?: string;   // NOTE: backend GET returns a URL here (in your current setup)
  latitude?: number | null;
  longitude?: number | null;
  cuid?: string;
  ruid?: string;
  info?: string;
};

const BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "";

async function parseResponse(res: Response) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : null;
  } catch {
    return text;
  }
}

// --------------------
// READ (unchanged)
// --------------------
export async function getLocations() {
  const res = await fetch(`${BASE_URL}/locations`);
  const data = await parseResponse(res);
  if (!res.ok) throw new Error(data?.error || "Failed to fetch locations");
  return data as StoreLocation[];
}

// --------------------
// SINGLE ENDPOINT: create/update + optional images (multipart/form-data)
// POST /locations/upsert
// Fields: id (optional), name, location, hours(JSON), number, map, latitude, longitude, cuid, ruid, info
// Files: mapImage (optional), storeImage (optional)
// --------------------
export async function upsertLocationMultipart(
  payload: Partial<Omit<StoreLocation, "id">> & { id?: string },
  files: { mapImage?: File | null; storeImage?: File | null },
  token: string
) {
  const form = new FormData();

  // update when present, create when absent
  if (payload.id) form.append("id", payload.id);

  // required
  form.append("name", payload.name ?? "");
  form.append("location", payload.location ?? "");
  form.append("hours", JSON.stringify(payload.hours ?? []));

  // optional text fields
  form.append("number", payload.number ?? "");
  form.append("map", payload.map ?? "");
  form.append("cuid", payload.cuid ?? "");
  form.append("ruid", payload.ruid ?? "");
  form.append("info", payload.info ?? "");

  // optional numeric fields (as strings)
  form.append("latitude", payload.latitude == null ? "" : String(payload.latitude));
  form.append("longitude", payload.longitude == null ? "" : String(payload.longitude));

  // optional files (field names MUST match backend: mapImage, storeImage)
  if (files.mapImage) form.append("mapImage", files.mapImage);
  if (files.storeImage) form.append("storeImage", files.storeImage);

  const res = await fetch(`${BASE_URL}/locations/upsert`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      // DO NOT set Content-Type for FormData
    },
    body: form,
  });

  const data = await parseResponse(res);
  if (!res.ok) {
    throw new Error(data?.error || "Failed to save location");
  }

  // backend can return extra fields like mapImgUrl/storeImgUrl; keep as any if you want
  return data as StoreLocation & { mapImgUrl?: string; storeImgUrl?: string };
}

// --------------------
// (Optional) Keep old JSON endpoints for now
// You can delete these later once AdminForm switches fully to upsertLocationMultipart.
// --------------------
export async function createLocation(payload: Omit<StoreLocation, "id">, token: string) {
  const res = await fetch(`${BASE_URL}/locations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseResponse(res);
  if (!res.ok) {
    console.error("CreateLocation failed:", data);
    throw new Error(data?.error || "Failed to create location");
  }
  return data as StoreLocation;
}

export async function updateLocation(
  id: string,
  payload: Partial<Omit<StoreLocation, "id">>,
  token: string
) {
  const res = await fetch(`${BASE_URL}/locations/${id}`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await parseResponse(res);
  if (!res.ok) {
    throw new Error(data?.error || "Failed to update location");
  }
  return data as StoreLocation;
}

export async function deleteLocation(id: string, token: string) {
  const res = await fetch(`${BASE_URL}/locations/${id}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  const data = await parseResponse(res);
  if (!res.ok) {
    throw new Error(data?.error || "Failed to delete location");
  }
  return data;
}

// --------------------
// (Optional) Keep old image-only endpoints for now
// You can delete these later once you're fully on /locations/upsert.
// --------------------
export async function uploadMapImage(file: File, token: string) {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${BASE_URL}/locations/map-image`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = await parseResponse(res);
  if (!res.ok) {
    throw new Error(data?.error || "Failed to upload map image");
  }
  return data as { path: string; url?: string };
}

export async function uploadStoreImage(file: File, token: string) {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${BASE_URL}/locations/store-image`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });
  const data = await parseResponse(res);
  if (!res.ok) {
    throw new Error(data?.error || "Failed to upload store image");
  }
  return data as { path: string; url?: string };
}